import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
} from '@/types/raspadinha-campaign'
import { findContemplatedPrizeById } from './contemplated-prize-images'

export type ContemplatedPrizeUsageRow = {
  prizeId: string
  title: string
  imageUrl: string
  maxContemplations: number | null
  configuredSlotCount: number
  fulfilledCount: number
  isConfiguredPrize: boolean
}

export type ContemplatedPrizeProgress = {
  scheduledCount: number
  fulfilledCount: number
}

const UNASSIGNED_PRIZE_ID = '__unassigned-prize__'

function countSlotsByPrizeId(
  schedules: readonly RaspadinhaDayWinningSchedule[]
): Map<string, number> {
  const counts = new Map<string, number>()

  for (const schedule of schedules) {
    for (const slot of schedule.time_slots) {
      const prizeId = slot.contemplated_prize_id.trim() || UNASSIGNED_PRIZE_ID
      counts.set(prizeId, (counts.get(prizeId) ?? 0) + 1)
    }
  }

  return counts
}

function countProgressByPrizeId(
  schedules: readonly RaspadinhaDayWinningSchedule[]
): Map<string, ContemplatedPrizeProgress> {
  const progress = new Map<string, ContemplatedPrizeProgress>()

  for (const schedule of schedules) {
    for (const slot of schedule.time_slots) {
      const prizeId = slot.contemplated_prize_id.trim() || UNASSIGNED_PRIZE_ID

      const current = progress.get(prizeId) ?? { scheduledCount: 0, fulfilledCount: 0 }
      progress.set(prizeId, {
        scheduledCount: current.scheduledCount + 1,
        fulfilledCount: current.fulfilledCount + slot.winners.length,
      })
    }
  }

  return progress
}

export function getContemplatedPrizeProgress(
  schedules: readonly RaspadinhaDayWinningSchedule[],
  prizeId: string
): ContemplatedPrizeProgress {
  if (!prizeId.trim()) {
    return { scheduledCount: 0, fulfilledCount: 0 }
  }

  return countProgressByPrizeId(schedules).get(prizeId) ?? {
    scheduledCount: 0,
    fulfilledCount: 0,
  }
}

export function buildContemplatedPrizeProgressMap(
  schedules: readonly RaspadinhaDayWinningSchedule[]
): Map<string, ContemplatedPrizeProgress> {
  return countProgressByPrizeId(schedules)
}

export function buildContemplatedPrizeUsageSummary(
  schedules: readonly RaspadinhaDayWinningSchedule[],
  contemplatedPrizes: readonly RaspadinhaContemplatedPrizeImage[]
): ContemplatedPrizeUsageRow[] {
  const slotCountsByPrizeId = countSlotsByPrizeId(schedules)
  const progressByPrizeId = countProgressByPrizeId(schedules)
  const rows: ContemplatedPrizeUsageRow[] = []

  for (const prize of contemplatedPrizes) {
    const title = prize.title.trim()
    if (!title) {
      continue
    }

    const progress = progressByPrizeId.get(prize.id)

    rows.push({
      prizeId: prize.id,
      title,
      imageUrl: prize.image_url,
      maxContemplations: prize.max_contemplations,
      configuredSlotCount: slotCountsByPrizeId.get(prize.id) ?? 0,
      fulfilledCount: progress?.fulfilledCount ?? 0,
      isConfiguredPrize: true,
    })
  }

  for (const [prizeId, configuredSlotCount] of slotCountsByPrizeId.entries()) {
    if (prizeId === UNASSIGNED_PRIZE_ID) {
      rows.push({
        prizeId,
        title: 'Sem prêmio definido',
        imageUrl: '',
        maxContemplations: null,
        configuredSlotCount,
        fulfilledCount: progressByPrizeId.get(prizeId)?.fulfilledCount ?? 0,
        isConfiguredPrize: false,
      })
      continue
    }

    if (contemplatedPrizes.some((prize) => prize.id === prizeId)) {
      continue
    }

    const orphanPrize = findContemplatedPrizeById([...contemplatedPrizes], prizeId)
    rows.push({
      prizeId,
      title: orphanPrize?.title.trim() || 'Prêmio indisponível',
      imageUrl: orphanPrize?.image_url ?? '',
      maxContemplations: orphanPrize?.max_contemplations ?? null,
      configuredSlotCount,
      fulfilledCount: progressByPrizeId.get(prizeId)?.fulfilledCount ?? 0,
      isConfiguredPrize: false,
    })
  }

  return rows.sort((left, right) => {
    if (right.configuredSlotCount !== left.configuredSlotCount) {
      return right.configuredSlotCount - left.configuredSlotCount
    }

    return left.title.localeCompare(right.title, 'pt-BR', { sensitivity: 'base' })
  })
}

export function countTotalConfiguredTimeSlots(
  schedules: readonly RaspadinhaDayWinningSchedule[]
): number {
  return schedules.reduce((total, schedule) => total + schedule.time_slots.length, 0)
}
